import { expectedColumns, requiredColumns } from "./columns";
import { isTechnicalPGrade, normalizePGrade } from "./grade";
import { stableId, uid } from "./id";
import { DEFAULT_LAYOUT_CONFIG } from "./layout";
import { DEFAULT_VIEW_CONFIG } from "./viewConfig";
import type { Employee, OrgIssue, OrgState, OrgUnit } from "../types/org";

type Row = Record<string, string>;

export interface NormalizedImportRows {
  employeeRoster: Row[];
  departmentCodes?: Row[];
  approvalHierarchy: Row[];
  performance?: Row[];
}

const levelColumns = ["Level 0", "Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Level 9"];
const employeeRosterRequiredColumns = [
  "工號",
  "BGBU",
  "公司代號",
  "組織代碼",
  "中文姓名",
  "英文名",
  "PGrade",
  "MGrade",
  "職類名稱",
  "職務名稱",
  "性別",
  "年資",
  "年齡",
  "地區",
  "組織中文名稱",
  "組織英文名稱",
];

export function createEmptyState(year = new Date().getFullYear(), month = new Date().getMonth() + 1): OrgState {
  return {
    periodKey: periodKeyFor(year, month),
    year,
    month,
    revision: 1,
    units: {},
    employees: {},
    edges: [],
    dashedEdges: [],
    manualParentOverrides: {},
    unassignedEmployeeIds: [],
    unlinkedUnitIds: [],
    issues: [],
    snapshots: [],
    changeLog: [],
    companyFilter: "Qisda",
    businessUnitFilter: "",
    searchQuery: "",
    language: "zh",
    layoutConfig: DEFAULT_LAYOUT_CONFIG,
    viewConfig: DEFAULT_VIEW_CONFIG,
  };
}

export function periodKeyFor(year: number, month: number) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function validateRows(kind: keyof typeof expectedColumns, rows: Row[]): OrgIssue[] {
  const headerSet = new Set(Object.keys(rows[0] ?? {}));
  const issues: OrgIssue[] = [];
  const required = kind === "employeeRoster" ? employeeRosterRequiredColumns : requiredColumns[kind];
  const expected = kind === "employeeRoster" ? employeeRosterRequiredColumns : expectedColumns[kind];

  for (const col of required) {
    if (!headerSet.has(col)) {
      issues.push({
        id: uid("issue"),
        severity: "error",
        status: "open",
        entity: "import",
        type: "Missing required column",
        description: `${labelForKind(kind)} 缺少必要欄位：${col}`,
        suggestedAction: "請確認 Excel 第一個工作表欄位名稱完全一致。",
      });
    }
  }

  for (const col of expected) {
    if (!headerSet.has(col)) {
      issues.push({
        id: uid("issue"),
        severity: "warning",
        status: "open",
        entity: "import",
        type: "Missing display column",
        description: `${labelForKind(kind)} 缺少顯示欄位：${col}`,
        suggestedAction: "此欄位不一定阻擋匯入，但相關畫面可能顯示空白。",
      });
    }
  }

  return issues;
}

export function buildOrgState(rows: NormalizedImportRows, year: number, month: number, carryOver?: OrgState): OrgState {
  const state = createEmptyState(year, month);
  copyCarryOverPresentationState(state, carryOver);
  const hasEmployeeRoster = rows.employeeRoster.length > 0;
  const hasApprovalHierarchy = rows.approvalHierarchy.length > 0;
  const hasDepartmentCodes = Boolean(rows.departmentCodes?.length);
  const issues: OrgIssue[] = [
    ...(hasEmployeeRoster ? validateRows("employeeRoster", rows.employeeRoster) : []),
    ...(hasApprovalHierarchy ? validateRows("approvalHierarchy", rows.approvalHierarchy) : []),
    ...(hasDepartmentCodes ? validateRows("departmentCodes", rows.departmentCodes!) : []),
    ...(rows.performance ? validateRows("performance", rows.performance) : []),
  ];

  const performanceByEmp = new Map<string, string[]>();
  for (const row of rows.performance ?? []) {
    const empNo = text(row["工號"]);
    if (!empNo) continue;
    performanceByEmp.set(empNo, [row["前一期績效"], row["前二期績效"], row["前三期績效"], row["前四期績效"]].map(text).filter(Boolean));
  }

  const approvalByEmp = buildApprovalMap(rows.approvalHierarchy);
  const seenApproval = new Set(approvalByEmp.keys());
  const carryOverUnitsByCode = groupBy(Object.values(carryOver?.units ?? {}), (unit) => normalizeCode(unit.code));

  const employeeNoCounts = new Map<string, number>();
  for (const row of rows.employeeRoster) {
    const empNo = text(row["工號"]);
    if (!empNo) {
      issues.push({
        id: uid("issue"),
        severity: "error",
        status: "open",
        entity: "employee",
        type: "Missing employee number",
        description: "員工清冊有資料列缺少工號，已忽略該列。",
        suggestedAction: "請補上工號後重新匯入。",
      });
      continue;
    }
    employeeNoCounts.set(empNo, (employeeNoCounts.get(empNo) ?? 0) + 1);
    const approval = approvalByEmp.get(empNo);
    const previousEmployee = carryOver?.employees[stableId("emp", [empNo])];
    const departmentCode = normalizeImportedDepartmentCode(text(row["組織代碼"]), carryOverUnitsByCode);
    const employee: Employee = {
      id: stableId("emp", [empNo]),
      employeeNo: empNo,
      chineseName: text(row["中文姓名"]) || approval?.name || empNo,
      englishName: text(row["英文名"]),
      companyCode: text(row["公司代號"]),
      buBg: text(row["BUBG"]),
      departmentCode,
      departmentNameZh: text(row["組織中文名稱"]),
      departmentNameEn: text(row["組織英文名稱"]),
      managerEmployeeNo: approval?.managerNo ?? previousEmployee?.managerEmployeeNo,
      managerName: approval?.managerName ?? previousEmployee?.managerName,
      pGrade: normalizePGrade(row["PGrade職等PB"]) ?? text(row["PGrade職等PB"]),
      mGrade: text(row["MGrade或JB"]),
      jobFamily: text(row["職類名稱"]),
      jobTitle: text(row["職務名稱"]),
      internalTitle: text(row["對內職稱或職階"]),
      externalTitle: text(row["對外職稱"]),
      gender: text(row["性別"]),
      tenure: text(row["年資"]),
      age: text(row["年齡"]),
      education: text(row["最高學歷"]),
      school: text(row["畢業院校"]),
      major: text(row["畢業科系"]),
      location: text(row["地區"]),
      hireDate: text(row["入職日"]),
      performance: performanceByEmp.get(empNo),
      source: "imported",
    };
    state.employees[employee.id] = employee;

    if (!approval?.managerNo && seenApproval.has(empNo)) {
      employee.pendingManager = true;
      issues.push(employeeIssue("warning", "待確認主管", employee, "簽核列表找不到可判定的直屬主管。", "請在待處理清單中改直屬主管。"));
    }
  }

  for (const employee of Object.values(carryOver?.employees ?? {})) {
    if (hasEmployeeRoster && employee.source !== "manual") continue;
    if (state.employees[employee.id]) continue;
    const nextEmployee = structuredClone(employee);
    const approval = hasApprovalHierarchy && nextEmployee.employeeNo ? approvalByEmp.get(nextEmployee.employeeNo) : undefined;
    if (approval) {
      nextEmployee.managerEmployeeNo = approval.managerNo;
      nextEmployee.managerName = approval.managerName;
      if (approval.managerNo) delete nextEmployee.pendingManager;
      else nextEmployee.pendingManager = true;
    }
    state.employees[nextEmployee.id] = nextEmployee;
  }

  for (const [empNo, count] of employeeNoCounts) {
    if (count > 1) {
      issues.push({
        id: uid("issue"),
        severity: "warning",
        status: "open",
        entity: "employee",
        type: "同一工號重複",
        employeeNo: empNo,
        description: `工號 ${empNo} 在員工清冊中出現 ${count} 次。`,
        suggestedAction: "請選擇保留資料或手動合併。",
      });
    }
  }

  if (!hasDepartmentCodes && carryOver) {
    state.units = structuredClone(carryOver.units);
    state.edges = structuredClone(carryOver.edges);
    state.dashedEdges = structuredClone(carryOver.dashedEdges);
    state.manualParentOverrides = structuredClone(carryOver.manualParentOverrides ?? {});
    state.unlinkedUnitIds = [...carryOver.unlinkedUnitIds];
    state.unassignedEmployeeIds = carryOver.unassignedEmployeeIds.filter((id) => Boolean(state.employees[id]));
    placeEmployees(state, issues, seenApproval);
    calculateHeadcounts(state);
    state.issues = issues;
    state.changeLog = [
      ...structuredClone(carryOver.changeLog ?? []),
      { id: uid("change"), label: "匯入新資料", detail: `${year}/${month} 已由 Excel 更新員工或簽核資料。` },
    ];
    return state;
  }

  rebuildImportedStructure(state, rows.departmentCodes!, carryOver, issues);
  placeEmployees(state, issues, seenApproval);
  calculateHeadcounts(state);
  state.dashedEdges = carryOver?.dashedEdges.filter((edge) => state.units[edge.source] && state.units[edge.target]) ?? [];
  state.issues = issues;
  state.changeLog = [
    ...structuredClone(carryOver?.changeLog ?? []),
    { id: uid("change"), label: "匯入新資料", detail: `${year}/${month} 已由 Excel 更新組織、員工與簽核資料。` },
  ];
  return state;
}

type ApprovalAssignment = { managerNo?: string; managerName?: string; deptCode?: string; name?: string };

function buildApprovalMap(rows: Row[]) {
  const loginToEmployee = new Map<string, string>();
  const loginToName = new Map<string, string>();
  for (const row of rows) {
    const employeeNo = text(row["Emp No"]);
    const login = normalizeLogin(row["Login Name"]);
    if (!employeeNo || !login) continue;
    loginToEmployee.set(login, employeeNo);
    loginToName.set(login, text(row["Name"]));
  }

  const assignments = new Map<string, ApprovalAssignment>();
  for (const row of rows) {
    const employeeNo = text(row["Emp No"]);
    if (!employeeNo) continue;
    const managerToken = findNearestManagerToken(row);
    const normalizedManager = normalizeLogin(managerToken);
    const managerNo = normalizedManager
      ? loginToEmployee.get(normalizedManager) ?? (/^\d+$/.test(managerToken) ? managerToken : undefined)
      : undefined;
    assignments.set(employeeNo, {
      managerNo,
      managerName: normalizedManager ? loginToName.get(normalizedManager) ?? managerToken : undefined,
      deptCode: text(row["Dept Code"]),
      name: text(row["Name"]),
    });
  }
  return assignments;
}

function copyCarryOverPresentationState(state: OrgState, carryOver?: OrgState) {
  if (!carryOver) return;
  state.manualParentOverrides = structuredClone(carryOver.manualParentOverrides ?? {});
  state.snapshots = structuredClone(carryOver.snapshots ?? []);
  state.layoutConfig = structuredClone(carryOver.layoutConfig ?? DEFAULT_LAYOUT_CONFIG);
  state.viewConfig = structuredClone(carryOver.viewConfig ?? DEFAULT_VIEW_CONFIG);
  state.companyFilter = carryOver.companyFilter;
  state.businessUnitFilter = carryOver.businessUnitFilter ?? "";
  state.language = carryOver.language;
  state.searchQuery = "";
}

function rebuildImportedStructure(state: OrgState, departmentRows: Row[], carryOver: OrgState | undefined, issues: OrgIssue[]) {
  const scopes = new Set(departmentRows.map((row) => text(row["__scope"])).filter(Boolean));
  const previousUnits = Object.values(carryOver?.units ?? {});
  const previousByScope = groupBy(previousUnits.filter((unit) => unit.source === "imported"), unitImportScope);
  const matchedPreviousIds = new Set<string>();

  for (const unit of previousUnits) {
    if (unit.source === "manual" || !scopes.has(unitImportScope(unit))) {
      state.units[unit.id] = structuredClone(unit);
    }
  }

  const stacks = new Map<string, Map<number, OrgUnit>>();
  let displayIndex = 0;
  for (const row of departmentRows) {
    const scope = text(row["__scope"]);
    const sourceSystem = text(row["__sourceSystem"]) || (scope === "qisda" ? "qisda" : "benq");
    const stack = stacks.get(scope) ?? new Map<number, OrgUnit>();
    stacks.set(scope, stack);
    const code = text(row["部門代碼"]);
    const nameZh = text(row["中文名稱"]);
    const mGrade = normalizeMGradeValue(row["M Grade"]);
    const matched = matchPreviousUnit(row, previousByScope.get(scope) ?? [], matchedPreviousIds);
    if (matched) matchedPreviousIds.add(matched.id);

    const explicitLevel = parseLevel(row["組織層級"]);
    let level = explicitLevel;
    let parentId: string | undefined;
    let isUnlinked = false;
    if (Number.isFinite(explicitLevel)) {
      pruneStack(stack, explicitLevel);
      parentId = findParentFromStack(stack, explicitLevel, mGrade);
    } else if (matched) {
      level = matched.level;
      parentId = matched.parentId && state.units[matched.parentId] ? matched.parentId : undefined;
      pruneStack(stack, level);
      if (!parentId) parentId = findParentFromStack(stack, level, mGrade);
    } else {
      const inferredParent = inferParentFromStack(stack, mGrade);
      if (inferredParent) {
        parentId = inferredParent.id;
        level = inferredParent.level + 1;
        pruneStack(stack, level);
      } else {
        level = Math.max(0, ...stack.keys()) + 1;
        isUnlinked = true;
      }
    }

    const id = matched?.id ?? stableId("unit", [scope, code || "no_code", nameZh || "unnamed", text(row["Manager ID"]), row["__rowIndex"] || displayIndex]);
    const unit: OrgUnit = {
      id,
      code,
      nameZh: nameZh || code || `未命名單位 ${displayIndex + 1}`,
      nameEn: text(row["English Name"]),
      level,
      parentId,
      mGrade,
      location: text(row["Location"]),
      managerEmployeeNo: text(row["Manager ID"]),
      managerNameZh: text(row["Manager中文名"]),
      managerNameEn: text(row["Manager英文名"]),
      secretaryEmployeeNo: text(row["秘書ID"]),
      secretaryNameZh: text(row["秘書中文名"]),
      secretaryNameEn: text(row["秘書英文名"]),
      isConcurrent: Boolean(text(row["兼"])),
      lineStyle: matched?.lineStyle ?? "solid",
      position: matched?.position ?? defaultPosition(displayIndex, level),
      slidePosition: matched?.slidePosition,
      slideManualOffset: matched?.slideManualOffset,
      slidePositionByRootId: matched?.slidePositionByRootId,
      slideManualOffsetByRootId: matched?.slideManualOffsetByRootId,
      staffExpanded: matched?.staffExpanded,
      expanded: matched?.expanded ?? false,
      headcount: 0,
      technicalHeadcount: 0,
      source: "imported",
      sourceSystem,
      buBg: text(row["BG/BU"]),
      isUnlinked,
    };
    state.units[id] = unit;
    stack.set(level, unit);
    displayIndex += 1;

    if (isUnlinked) {
      issues.push(unitIssue(
        "error",
        "無法判定上層部門",
        unit,
        `${unit.nameZh} 缺少組織層級，且無法安全推導上層單位。`,
        "請補上 Level/組織層級後重新匯入。",
      ));
    }
  }

  for (const unit of Object.values(state.units)) {
    if (unit.source !== "manual" || !unit.parentId || state.units[unit.parentId]) continue;
    unit.parentId = undefined;
    unit.isUnlinked = true;
    issues.push(unitIssue("warning", "手動單位失去上層", unit, `${unit.nameZh} 的原上層已由部門代碼表刪除。`, "請重新指定上層單位。"));
  }

  const previousOverrides = carryOver?.manualParentOverrides ?? {};
  state.manualParentOverrides = Object.fromEntries(Object.entries(previousOverrides).filter(([unitId, parentId]) => {
    const unit = state.units[unitId];
    if (!unit || (parentId && !state.units[parentId])) return false;
    return unit.source === "manual" || !scopes.has(unitImportScope(unit));
  }));
  rebuildHierarchyEdges(state);
}

function matchPreviousUnit(row: Row, previous: OrgUnit[], matchedIds: Set<string>) {
  const available = previous.filter((unit) => !matchedIds.has(unit.id));
  const code = normalizeCode(row["部門代碼"]);
  const name = normalizeText(row["中文名稱"] || row["English Name"]);
  const manager = text(row["Manager ID"]);
  const mGrade = normalizeMGradeValue(row["M Grade"]);
  const matchUnique = (predicate: (unit: OrgUnit) => boolean) => {
    const matches = available.filter(predicate);
    return matches.length === 1 ? matches[0] : undefined;
  };
  return matchUnique((unit) => normalizeCode(unit.code) === code && normalizeText(unit.nameZh || unit.nameEn) === name)
    ?? matchUnique((unit) => normalizeCode(unit.code) === code && text(unit.managerEmployeeNo) === manager && normalizeMGradeValue(unit.mGrade) === mGrade)
    ?? matchUnique((unit) => normalizeCode(unit.code) === code && normalizeMGradeValue(unit.mGrade) === mGrade)
    ?? matchUnique((unit) => normalizeCode(unit.code) === code);
}

function unitImportScope(unit: OrgUnit) {
  if (unit.sourceSystem?.toLowerCase() === "qisda") return "qisda";
  const business = text(unit.buBg).toLowerCase();
  return business === "bqp" || business === "bqtw" ? "bqp" : "benq";
}

function parseLevel(value: unknown) {
  const match = text(value).match(/\d+/);
  return match ? Number(match[0]) : Number.NaN;
}

function inferParentFromStack(stack: Map<number, OrgUnit>, childMGrade: string) {
  const childRank = mGradeRank(childMGrade);
  return [...stack.values()].reverse().find((candidate) => {
    const parentRank = mGradeRank(candidate.mGrade);
    return childRank ? parentRank > childRank : true;
  });
}

function normalizeMGradeValue(value: unknown) {
  const raw = text(value).toUpperCase();
  if (!raw) return "";
  const match = raw.match(/(?:M\s*)?(\d+)/);
  return match ? `M${match[1]}` : raw;
}

function normalizeText(value: unknown) {
  return text(value).replace(/\s+/g, " ").toLowerCase();
}

export function applyKnownHierarchyOverrides(state: OrgState) {
  const overrides = [
    { childCode: "C000", childManagerEmployeeNo: "2603009", parentCode: "E000", parentManagerEmployeeNo: "9108086" },
    { childCode: "AL00", childManagerEmployeeNo: "1912019", parentCode: "C000", parentManagerEmployeeNo: "2603009" },
  ];

  let changed = false;
  for (const override of overrides) {
    const child = findImportedUnit(state, override.childCode, override.childManagerEmployeeNo);
    const parent = findImportedUnit(state, override.parentCode, override.parentManagerEmployeeNo);
    if (!child || !parent || child.id === parent.id || wouldCreateUnitCycle(state, child.id, parent.id)) continue;
    if (child.parentId === parent.id && child.isUnlinked === false) continue;
    child.parentId = parent.id;
    child.isUnlinked = false;
    changed = true;
  }

  if (!changed) return false;
  rebuildHierarchyEdges(state);
  return true;
}

export function applyManualParentOverrides(state: OrgState) {
  const overrides = state.manualParentOverrides ?? {};
  let changed = false;
  for (const [unitId, parentId] of Object.entries(overrides)) {
    const unit = state.units[unitId];
    if (!unit) continue;
    const parent = parentId ? state.units[parentId] : undefined;
    if (parentId && (!parent || unit.id === parentId || wouldCreateUnitCycle(state, unit.id, parentId))) continue;
    if (unit.parentId === (parentId ?? undefined) && unit.isUnlinked === false) continue;
    unit.parentId = parentId ?? undefined;
    unit.isUnlinked = false;
    changed = true;
  }
  if (!changed) return false;
  rebuildHierarchyEdges(state);
  return true;
}

function findImportedUnit(state: OrgState, code: string, managerEmployeeNo: string) {
  return Object.values(state.units).find((unit) => unit.source === "imported" && unit.code === code && unit.managerEmployeeNo === managerEmployeeNo);
}

function wouldCreateUnitCycle(state: OrgState, unitId: string, parentId: string) {
  let current = parentId;
  while (current) {
    if (current === unitId) return true;
    current = state.units[current]?.parentId ?? "";
  }
  return false;
}

function rebuildHierarchyEdges(state: OrgState) {
  const existingEdges = new Map(state.edges.map((edge) => [`${edge.source}->${edge.target}`, edge]));
  state.edges = Object.values(state.units)
    .filter((unit) => unit.parentId && !unit.isUnlinked)
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || a.id.localeCompare(b.id))
    .map((unit) => existingEdges.get(`${unit.parentId}->${unit.id}`) ?? {
      id: stableId("edge", [unit.parentId!, unit.id]),
      source: unit.parentId!,
      target: unit.id,
      style: unit.lineStyle,
    });
  state.unlinkedUnitIds = Object.values(state.units).filter((unit) => unit.isUnlinked).map((unit) => unit.id);
}

function placeEmployees(state: OrgState, issues: OrgIssue[], approvalEmployeeNos = new Set<string>()) {
  const units = Object.values(state.units);
  const unitsByCode = groupBy(units, (unit) => normalizeCode(unit.code));
  const unitsByManager = groupBy(units, (unit) => unit.managerEmployeeNo ?? "");
  const employeesByNo = new Map(Object.values(state.employees).filter((employee) => employee.employeeNo).map((employee) => [employee.employeeNo, employee]));
  state.unassignedEmployeeIds = [];

  for (const employee of Object.values(state.employees)) {
    const departmentCode = normalizeImportedDepartmentCode(employee.departmentCode, unitsByCode);
    employee.departmentCode = departmentCode || employee.departmentCode;
    const ownedManagerUnits = employee.employeeNo ? unitsByManager.get(employee.employeeNo) ?? [] : [];
    const sameDeptUnits = departmentCode ? unitsByCode.get(normalizeCode(departmentCode)) ?? [] : [];
    const currentUnit = employee.unitId ? state.units[employee.unitId] : undefined;
    const sameCompany = (unit: OrgUnit) => unitMatchesEmployeeCompany(unit, employee, currentUnit);
    const sameCompanyDepartmentUnits = sameDeptUnits.filter(sameCompany);
    const departmentCandidates = sameCompanyDepartmentUnits.length ? sameCompanyDepartmentUnits : sameDeptUnits;
    const ownedDepartmentUnits = ownedManagerUnits.filter((unit) => departmentCandidates.some((candidate) => candidate.id === unit.id));
    const directManagerUnits = employee.managerEmployeeNo
      ? departmentCandidates.filter((unit) => unit.managerEmployeeNo === employee.managerEmployeeNo)
      : [];
    const currentIsCandidate = Boolean(currentUnit && departmentCandidates.some((unit) => unit.id === currentUnit.id));
    const managerUnits = employee.managerEmployeeNo
      ? (unitsByManager.get(employee.managerEmployeeNo) ?? []).filter(sameCompany)
      : [];

    let candidate: OrgUnit | undefined = preferredOwnedUnit(ownedDepartmentUnits);
    if (!candidate && directManagerUnits.length) candidate = uniquePreferredUnit(directManagerUnits, currentUnit);
    if (!candidate && !approvalEmployeeNos.has(employee.employeeNo) && currentIsCandidate && currentUnit) candidate = currentUnit;
    if (!candidate && departmentCandidates.length === 1) candidate = departmentCandidates[0];
    if (!candidate && !departmentCandidates.length) candidate = uniquePreferredUnit(managerUnits, currentUnit);

    if (candidate) {
      employee.unitId = candidate.id;
      employee.departmentCode = candidate.code || employee.departmentCode;
      employee.departmentNameZh = candidate.nameZh;
      employee.departmentNameEn = candidate.nameEn;
    } else {
      if (!state.unassignedEmployeeIds.includes(employee.id)) state.unassignedEmployeeIds.push(employee.id);
      const ambiguous = departmentCandidates.length > 1 || managerUnits.length > 1;
      issues.push(employeeIssue(
        "warning",
        ambiguous ? "無法唯一判定部門" : "找不到部門",
        employee,
        ambiguous
          ? `${employee.chineseName} 有多個同代碼單位，無法依簽核主管唯一判定。`
          : `${employee.chineseName} 找不到可放置的部門。`,
        "請在待處理清單中指派到正確單位。",
      ));
    }
  }

  for (const unit of Object.values(state.units)) {
    if (!unit.managerEmployeeNo && !unit.managerNameZh) {
      issues.push(unitIssue("warning", "找不到主管", unit, `${unit.nameZh} 主管未指定。`, "請在單位屬性中設定主管。"));
    } else if (unit.managerEmployeeNo && !employeesByNo.has(unit.managerEmployeeNo)) {
      issues.push(unitIssue("warning", "主管不在員工名冊", unit, `${unit.nameZh} 的主管 ${unit.managerNameZh || unit.managerEmployeeNo} 不在本次員工資料中。`, "請確認員工名冊或保留部門代碼表姓名。"));
    }
  }
}

function preferredOwnedUnit(units: OrgUnit[]) {
  const primary = units.filter((unit) => !unit.isConcurrent);
  return [...(primary.length ? primary : units)].sort(compareHomeUnit)[0];
}

function uniquePreferredUnit(units: OrgUnit[], currentUnit?: OrgUnit) {
  if (units.length === 1) return units[0];
  const primary = units.filter((unit) => !unit.isConcurrent);
  if (primary.length === 1) return primary[0];
  if (currentUnit && units.some((unit) => unit.id === currentUnit.id)) return currentUnit;
  return undefined;
}

function compareHomeUnit(a: OrgUnit, b: OrgUnit) {
  return mGradeRank(b.mGrade) - mGradeRank(a.mGrade) || a.level - b.level || a.id.localeCompare(b.id);
}

export function reconcileManagerAssignments(state: OrgState): OrgState {
  const units = Object.values(state.units);
  const unitsByManager = groupBy(units.filter((unit) => !unit.isUnlinked), (unit) => unit.managerEmployeeNo ?? "");
  for (const employee of Object.values(state.employees)) {
    if (!employee.employeeNo || employee.isDraft || state.unassignedEmployeeIds.includes(employee.id)) continue;
    const currentUnit = employee.unitId ? state.units[employee.unitId] : undefined;
    const managerUnits = unitsByManager.get(employee.employeeNo) ?? [];

    // An employee's confirmed unit is their home assignment and must not be
    // overwritten merely because another unit references them as manager.
    // Interactive edits mark the edited unit concurrent at save time; this
    // reconciliation only falls back to manager placement when no valid home
    // assignment exists yet.
    if (isValidEmployeeHome(employee, currentUnit)) {
      continue;
    }

    const managerUnit = preferredManagerUnit(employee, managerUnits, currentUnit);
    if (!managerUnit || managerUnit.id === employee.unitId) continue;
    employee.unitId = managerUnit.id;
    employee.departmentCode = managerUnit.code || employee.departmentCode;
    employee.departmentNameZh = managerUnit.nameZh;
    employee.departmentNameEn = managerUnit.nameEn;
    employee.buBg = managerUnit.buBg || employee.buBg;
  }
  return state;
}

const MANUAL_EMPLOYEE_OVERRIDE_FIELDS: Array<keyof Employee> = [
  "unitId",
  "draftUnitId",
  "departmentCode",
  "departmentNameZh",
  "departmentNameEn",
  "managerEmployeeNo",
  "managerName",
  "companyCode",
  "buBg",
  "isSecretary",
  "isConcurrentManager",
  "pendingManager",
];

/**
 * A copied period can contain both the roster record and a previously saved
 * manual record for the same employee. Keep the roster record as the canonical
 * profile, but preserve the manual record's structural edits before removing it.
 */
export function reconcileDuplicateEmployeeRecords(state: OrgState): boolean {
  const importedByEmployeeNo = new Map<string, Employee>();
  const manualRecords: Employee[] = [];

  for (const employee of Object.values(state.employees)) {
    const employeeNo = employee.employeeNo.trim();
    if (!employeeNo || employeeNo === "NEW" || employee.isDraft) continue;
    if (employee.source === "imported") importedByEmployeeNo.set(employeeNo, employee);
    else if (employee.source === "manual") manualRecords.push(employee);
  }

  const replacedIds = new Map<string, string>();
  for (const manual of manualRecords) {
    const imported = importedByEmployeeNo.get(manual.employeeNo.trim());
    if (!imported || imported.id === manual.id) continue;
    for (const field of MANUAL_EMPLOYEE_OVERRIDE_FIELDS) {
      if (manual[field] !== undefined) imported[field] = manual[field] as never;
    }
    replacedIds.set(manual.id, imported.id);
    delete state.employees[manual.id];
  }

  if (!replacedIds.size) return false;
  state.unassignedEmployeeIds = state.unassignedEmployeeIds
    .map((id) => replacedIds.get(id) ?? id)
    .filter((id, index, ids) => Boolean(state.employees[id]) && ids.indexOf(id) === index);
  if (state.selectedEmployeeId) state.selectedEmployeeId = replacedIds.get(state.selectedEmployeeId) ?? state.selectedEmployeeId;
  return true;
}

const KNOWN_CONCURRENT_MANAGER_HOME_OVERRIDES: Record<string, { sourceSystem: string; unitCode: string }> = {
  "2203003": { sourceSystem: "benq", unitCode: "BL00" },
  "9802012": { sourceSystem: "benq", unitCode: "BH00" },
};

/**
 * Older drafts may already have had a concurrent manager's home assignment
 * overwritten by the managed unit. Repair only the known affected employees,
 * and only while the saved assignment is missing or still points to a unit
 * managed by that employee. The managed unit may itself have been saved with
 * an incorrect non-concurrent flag, so that flag cannot be used as evidence.
 */
export function reconcileKnownConcurrentManagerHomes(state: OrgState): boolean {
  let changed = false;
  const units = Object.values(state.units);

  for (const employee of Object.values(state.employees)) {
    const override = KNOWN_CONCURRENT_MANAGER_HOME_OVERRIDES[employee.employeeNo.trim()];
    if (!override || employeeCompanySource(employee) !== override.sourceSystem) continue;
    const currentUnit = employee.unitId ? state.units[employee.unitId] : undefined;
    const isAlreadyHome = Boolean(
      currentUnit?.sourceSystem === override.sourceSystem && sameToken(currentUnit.code, override.unitCode),
    );
    if (isAlreadyHome) continue;
    const pointsToManagedUnit = Boolean(currentUnit?.managerEmployeeNo === employee.employeeNo);
    if (currentUnit && !pointsToManagedUnit) continue;

    const homeUnit = units.find((unit) =>
      !unit.isUnlinked &&
      unit.sourceSystem === override.sourceSystem &&
      sameToken(unit.code, override.unitCode),
    );
    if (!homeUnit) continue;

    employee.unitId = homeUnit.id;
    employee.draftUnitId = undefined;
    employee.departmentCode = homeUnit.code;
    employee.departmentNameZh = homeUnit.nameZh;
    employee.departmentNameEn = homeUnit.nameEn;
    employee.managerEmployeeNo = homeUnit.managerEmployeeNo;
    employee.managerName = homeUnit.managerNameZh;
    employee.buBg = homeUnit.buBg || employee.buBg;
    state.unassignedEmployeeIds = state.unassignedEmployeeIds.filter((id) => id !== employee.id);
    changed = true;
  }

  return changed;
}

export function calculateHeadcounts(state: OrgState): OrgState {
  reconcileDuplicateEmployeeRecords(state);
  reconcileKnownConcurrentManagerHomes(state);
  reconcileManagerAssignments(state);
  for (const unit of Object.values(state.units)) {
    unit.headcount = 0;
    unit.technicalHeadcount = 0;
  }
  const units = Object.values(state.units);
  const children = groupBy(units, (unit) => unit.parentId ?? "");
  const unitsByCode = groupBy(units.filter((unit) => !unit.isUnlinked), (unit) => normalizeCode(unit.code));
  const unitsByManager = groupBy(units.filter((unit) => !unit.isUnlinked), (unit) => unit.managerEmployeeNo ?? "");
  const employeesByNo = new Map(Object.values(state.employees).filter((employee) => employee.employeeNo).map((employee) => [employee.employeeNo, employee]));

  const directCounts = new Map<string, number>();
  const technicalDirectCounts = new Map<string, number>();
  const countedManagerKeys = new Set<string>();
  for (const employee of Object.values(state.employees)) {
    if (!employee.unitId || state.unassignedEmployeeIds.includes(employee.id)) continue;
    const unit = state.units[employee.unitId];
    if (!unit || unit.isUnlinked) continue;
    const countUnit = headcountUnitForEmployee(employee, unit, unitsByCode, unitsByManager);
    if (!countUnit) continue;
    if (countUnit.isConcurrent && employee.employeeNo === countUnit.managerEmployeeNo) continue;
    incrementHeadcount(isTechnicalPGrade(employee.pGrade) ? technicalDirectCounts : directCounts, countUnit.id);
    if (employee.employeeNo && countUnit.managerEmployeeNo === employee.employeeNo) {
      countedManagerKeys.add(`${countUnit.id}:${employee.employeeNo}`);
    }
  }

  for (const unit of units) {
    if (unit.isUnlinked || unit.isConcurrent || (!unit.managerEmployeeNo && !unit.managerNameZh)) continue;
    const managerKey = unit.managerEmployeeNo ? `${unit.id}:${unit.managerEmployeeNo}` : "";
    if (managerKey && countedManagerKeys.has(managerKey)) continue;
    const manager = unit.managerEmployeeNo ? employeesByNo.get(unit.managerEmployeeNo) : undefined;
    if (manager) {
      const managerUnit = manager.unitId ? state.units[manager.unitId] : unit;
      if (managerUnit && headcountUnitForEmployee(manager, managerUnit, unitsByCode, unitsByManager)?.id !== unit.id) continue;
    }
    incrementHeadcount(manager && isTechnicalPGrade(manager.pGrade) ? technicalDirectCounts : directCounts, unit.id);
  }

  const visit = (unit: OrgUnit): { headcount: number; technicalHeadcount: number } => {
    let total = directCounts.get(unit.id) ?? 0;
    let technicalTotal = technicalDirectCounts.get(unit.id) ?? 0;
    for (const child of children.get(unit.id) ?? []) {
      if (child.isUnlinked) continue;
      const childCounts = visit(child);
      total += childCounts.headcount;
      technicalTotal += childCounts.technicalHeadcount;
    }
    unit.headcount = total;
    unit.technicalHeadcount = technicalTotal;
    return { headcount: total, technicalHeadcount: technicalTotal };
  };

  for (const unit of Object.values(state.units)) {
    if (!unit.parentId && !unit.isUnlinked) visit(unit);
  }
  return state;
}

function headcountUnitForEmployee(
  employee: Employee,
  currentUnit: OrgUnit,
  unitsByCode: Map<string, OrgUnit[]>,
  unitsByManager: Map<string, OrgUnit[]>,
) {
  const departmentCode = normalizeImportedDepartmentCode(employee.departmentCode, unitsByCode);
  const source = employeeCompanySource(employee);
  if (
    departmentCode &&
    sameToken(currentUnit.code, departmentCode) &&
    (!source || unitMatchesEmployeeCompany(currentUnit, employee))
  ) {
    return currentUnit;
  }

  const departmentUnits = departmentCode ? unitsByCode.get(normalizeCode(departmentCode)) ?? [] : [];
  const sameCompanyDepartmentUnits = departmentUnits.filter((unit) => unitMatchesEmployeeCompany(unit, employee, currentUnit));
  const departmentCandidates = sameCompanyDepartmentUnits.length ? sameCompanyDepartmentUnits : departmentUnits;
  const departmentUnit =
    deepest(departmentCandidates.filter((unit) => !unit.isConcurrent)) ??
    deepest(departmentCandidates);
  if (departmentUnit && (!source || unitMatchesEmployeeCompany(departmentUnit, employee, currentUnit))) return departmentUnit;

  const ownedManagerUnits = employee.employeeNo ? unitsByManager.get(employee.employeeNo) ?? [] : [];
  const sameCompanyManagerUnits = ownedManagerUnits.filter((unit) => unitMatchesEmployeeCompany(unit, employee, currentUnit));
  const managerCandidates = sameCompanyManagerUnits.length || source ? sameCompanyManagerUnits : ownedManagerUnits;
  const currentHomeUnit = source && !unitMatchesEmployeeCompany(currentUnit, employee) ? undefined : currentUnit;
  return (
    deepest(managerCandidates.filter((unit) => !unit.isConcurrent)) ??
    currentHomeUnit ??
    deepest(managerCandidates)
  );
}

function incrementHeadcount(counts: Map<string, number>, unitId: string) {
  counts.set(unitId, (counts.get(unitId) ?? 0) + 1);
}

function preferredManagerUnit(employee: Employee, managerUnits: OrgUnit[], currentUnit?: OrgUnit) {
  const sameCompanyUnits = managerUnits.filter((unit) => unitMatchesEmployeeCompany(unit, employee, currentUnit));
  const candidates = sameCompanyUnits.length ? sameCompanyUnits : managerUnits;
  const primaryCandidates = candidates.filter((unit) => !unit.isConcurrent);
  if (!primaryCandidates.length) return undefined;
  const departmentCode = normalizeCode(employee.departmentCode);
  return (
    deepest(primaryCandidates.filter((unit) => sameToken(unit.code, departmentCode))) ??
    deepest(primaryCandidates)
  );
}

function unitMatchesEmployeeCompany(unit: OrgUnit, employee: Employee, currentUnit?: OrgUnit) {
  const scope = employeeImportScope(employee);
  if (scope) return unitImportScope(unit) === scope;
  return !currentUnit?.sourceSystem || unit.sourceSystem === currentUnit.sourceSystem;
}

function employeeImportScope(employee: Employee) {
  const companyCode = employee.companyCode?.toLowerCase() ?? "";
  const business = employee.buBg?.trim().toLowerCase() ?? "";
  if (companyCode.includes("qisda")) return "qisda";
  if (business === "bqp" || business === "bqtw" || companyCode.includes("bqp") || companyCode.includes("asia pacific")) return "bqp";
  if (companyCode.includes("benq")) return "benq";
  return undefined;
}

function isValidEmployeeHome(employee: Employee, unit?: OrgUnit): unit is OrgUnit {
  if (!unit || unit.isUnlinked) return false;
  const departmentCode = normalizeCode(employee.departmentCode);
  if (departmentCode && !sameToken(unit.code, departmentCode)) return false;
  return unitMatchesEmployeeCompany(unit, employee, unit);
}

function employeeCompanySource(employee: Employee) {
  const companyCode = employee.companyCode?.toLowerCase() ?? "";
  if (companyCode.includes("benq")) return "benq";
  if (companyCode.includes("qisda")) return "qisda";
  return undefined;
}

function findNearestManagerToken(row: Row) {
  const self = normalizeLogin(row["Login Name"]);
  for (let index = levelColumns.length - 1; index >= 0; index -= 1) {
    const people = text(row[levelColumns[index]]).split(">").map(text).filter(Boolean);
    const manager = people.find((person) => normalizeLogin(person) !== self);
    if (manager) return manager;
  }
  return "";
}

function normalizeLogin(value: unknown) {
  return text(value).replace(/\s+/g, "").toLowerCase();
}

function sameToken(value: unknown, token: string) {
  return String(value ?? "").trim().toLowerCase() === token.trim().toLowerCase();
}

function normalizeCode(value: unknown) {
  return text(value).toUpperCase();
}

function normalizeImportedDepartmentCode(value: unknown, unitsByCode: Map<string, OrgUnit[]>) {
  const code = normalizeCode(value);
  if (!code) return "";
  const exactUnits = unitsByCode.get(code);
  if (exactUnits?.[0]?.code) return exactUnits[0].code;

  if (code.endsWith("A")) {
    const parentCode = code.slice(0, -1);
    const parentUnits = unitsByCode.get(parentCode);
    if (parentUnits?.[0]?.code) return parentUnits[0].code;
    if (parentUnits?.length) return parentCode;
  }

  return code;
}

function findParentFromStack(stack: Map<number, OrgUnit>, level: number, childMGrade = "") {
  const childRank = mGradeRank(childMGrade);
  for (let target = level - 1; target >= 0; target -= 1) {
    const parent = stack.get(target);
    if (!parent) continue;
    if (childRank) {
      const parentRank = mGradeRank(parent.mGrade);
      if (parentRank && parentRank <= childRank) continue;
    }
    return parent.id;
  }
  return undefined;
}

function mGradeRank(value?: string) {
  const match = text(value).match(/^(?:M\s*)?(\d+)$/i);
  return match ? Number(match[1]) : 0;
}

function defaultPosition(index: number, level: number) {
  return { x: level * 310, y: index * 96 };
}

function deepest(units: OrgUnit[]) {
  return [...units].sort((a, b) => b.level - a.level)[0];
}

function pruneStack(stack: Map<number, OrgUnit>, level: number) {
  for (const key of [...stack.keys()]) {
    if (key >= level) stack.delete(key);
  }
}

function groupBy<T>(items: T[], getKey: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = getKey(item);
    map.set(key, [...(map.get(key) ?? []), item]);
  }
  return map;
}

function text(value: unknown): string {
  return String(value ?? "").trim();
}

function labelForKind(kind: keyof typeof expectedColumns) {
  return {
    employeeRoster: "員工清冊",
    departmentCodes: "部門代碼表",
    approvalHierarchy: "簽核列表",
    performance: "績效評核表",
  }[kind];
}

function employeeIssue(severity: "error" | "warning", type: string, employee: Employee, description: string, suggestedAction: string): OrgIssue {
  return {
    id: uid("issue"),
    severity,
    status: "open",
    entity: "employee",
    type,
    employeeNo: employee.employeeNo,
    employeeName: employee.chineseName,
    departmentCode: employee.departmentCode,
    description,
    suggestedAction,
  };
}

function unitIssue(severity: "error" | "warning", type: string, unit: OrgUnit, description: string, suggestedAction: string): OrgIssue {
  return {
    id: uid("issue"),
    severity,
    status: "open",
    entity: "unit",
    type,
    unitId: unit.id,
    departmentCode: unit.code,
    description,
    suggestedAction,
  };
}
